import { Grid, TextField, TextFieldProps } from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import React, { useState } from "react";
import { IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CustomTooltip from "./customTooltip";

export type CustomTextFieldProps = Omit<TextFieldProps, "name"> & {
  validation?: ControllerProps["rules"];
  name: string;
  toolTipTitle?: string;
  control?: Control<any>;
  readOnly?: boolean;
  autoComplete?: string;
  length?: number;
  label?: string;
};

export default function CustomPasswordField({
  validation = {},
  type,
  required,
  name,
  toolTipTitle,
  readOnly,
  autoComplete,
  length,
  control,
  label,
  ...rest
}: CustomTextFieldProps): JSX.Element {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const [password, setPassword] = useState<boolean>(true);

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({ field: { value }, fieldState: { error } }) => (
        <Grid container alignItems="center">
          <Grid item>
            <Grid container alignItems="center">
              <Grid item>
                <Grid container alignItems="center">
                  <Grid
                    item
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Poppins",
                      fontWeight: "300",
                      mb: "4px",
                      ml: "4px",
                      mr: "6px",
                      color: "#797979",
                    }}
                  >
                    {label}
                  </Grid>
                  {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            {...rest}
            value={value || ""}
            required={required}
            type={password ? "password" : "text"}
            {...register(name)}
            inputProps={{
              maxLength: length ? length : null,
              readOnly: readOnly ? readOnly : false,
              autoComplete: autoComplete ? autoComplete : null,
            }}
            sx={{
              borderRadius: "100px",
              marginBottom: "14px",
              "& .MuiInputLabel-root": {
                color: "#CCCCCC", // Label color
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "100px",
                fontSize: "16px",
                      fontWeight: "200",
                "& fieldset": {
                  borderRadius: "100px",
                  color: "#000000",
                },
                "&:hover fieldset": {
                  borderColor: "#CCCCCC", // Hovered border color
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000", // Focused border color
                },
                "& input": {
                  color: "#000000", // Text color inside input
                },
              },
              "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px transparent inset",
                      WebkitTextFillColor: "#000000",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
            }}
            error={!!error}
            helperText={error ? error.message : rest?.helperText}
            InputProps={{
              endAdornment: (
                <InputAdornment position={"end"}>
                  <IconButton
                  sx={{
                    color:'#606060'
                  }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setPassword(!password)}
                    tabIndex={-1}
                  >
                    {password ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}
    />
  );
}
